/** @jsxImportSource @gasman/smush */

import './sample_editor.css';

import { Model, fields } from '@gasman/smush';
import { ContainerComponent } from '@gasman/smush';

import { SampleGraph } from '../widgets/sample_graph';
import { Bitmask } from '../widgets/bitmask';
import { Counter } from '../widgets/counter';
import { NumberColumn } from '../widgets/number_column';

class SampleEditorState extends Model([
    new fields.IntegerField('sampleNumber', {
        label: 'Sample',
        min: 1,
        max: 15,
        default: 1,
    }),
]) {}

class SampleEditor extends ContainerComponent {
    constructor(app, model) {
        super();

        this.app = app;
        this.state = null;

        this.sampleSelector = Counter.forField(
            SampleEditorState.fields.sampleNumber,
        );

        this.repeatSelector = Counter.forField(model.fields.repeat);
        this.repeatLengthSelector = Counter.forField(model.fields.repeatLength);

        this.auditionButton = <button>Test</button>;
        this.auditionButton.addEventListener('mousedown', (event) => {
            // only trigger on left mouse button
            if (event.button != 0) return;

            if (this.state) {
                this.app.player.auditionSample(this.state.sampleNumber);
            }
        });
        this.auditionButton.addEventListener('mouseup', (event) => {
            // only trigger on left mouse button
            if (event.button != 0) return;

            this.app.player.stop();
        });

        this.volumeGraph = SampleGraph.forField(model.fields.volumes, {
            vertical: true,
        });
        this.toneBitmask = Bitmask.forField(model.fields.toneMasks, {
            vertical: true,
        });
        this.noiseGraph = SampleGraph.forField(model.fields.noiseLevels, {
            vertical: true,
            className: 'slim',
        });

        this.noiseBitmask = Bitmask.forField(model.fields.noiseMasks, {
            vertical: true,
        });

        this.toneColumn = NumberColumn.forField(model.fields.tones, {
            className: 'tone-column',
        });

        this.samplePropertyEditors = [
            this.volumeGraph,
            this.toneBitmask,
            this.noiseGraph,
            this.noiseBitmask,
            this.repeatSelector,
            this.repeatLengthSelector,
            this.toneColumn,
        ];
        this.components = [
            this.sampleSelector,
            this.volumeGraph,
            this.toneBitmask,
            this.noiseGraph,
            this.noiseBitmask,
            this.repeatSelector,
            this.repeatLengthSelector,
            this.toneColumn,
        ];
    }

    render(container) {
        container.appendChild(
            <div class="sample-editor">
                {this.sampleSelector}
                {this.repeatSelector}
                {this.repeatLengthSelector}
                {this.auditionButton}
                <div class="graphs">
                    {this.toneBitmask}
                    {this.volumeGraph}
                    {this.noiseBitmask}
                    {this.noiseGraph}
                    {this.toneColumn}
                </div>
            </div>,
        );
    }

    unbindSample() {
        this.samplePropertyEditors.forEach((component) => {
            component.unbind();
        });
    }

    bind(song, opts) {
        opts = opts || {};

        this.unbindSample();
        this.song = song;
        this.state = new SampleEditorState();
        this.undoBuffer = opts.undoBuffer;
        this.undoPath = opts.undoPath;

        this.selectSample(this.state.sampleNumber);
        this.state.on('changeSampleNumber', this.selectSample);
        this.sampleSelector.bind(this.state);
    }
    unbind() {
        this.unbindSample();
        this.song = null;
        this.state.removeListener('changeSampleNumber', this.selectSample);
        this.state = null;
        this.undoBuffer = null;
        this.undoPath = null;
    }

    selectSample = (sampleNumber) => {
        let sample = this.song.getSample(sampleNumber);

        let opts = {};
        if (this.undoBuffer) {
            opts.undoBuffer = this.undoBuffer;
            opts.undoPath = [...this.undoPath, sampleNumber];
        }

        this.samplePropertyEditors.forEach((component) => {
            component.bind(sample, opts);
        });
    };
}

export { SampleEditor };
